import React, { useState } from "react";
import { m as motion } from "../../shared/components/Motion";
import WorksSkeleton from "./WorksSkeleton";
import { useProjectsData } from "../../shared/hooks";
import { useAdmin } from "../../features/admin/AdminProvider";
import EditProjectModal from "../../features/admin/modals/EditProjectModal";
import WorkItems from "./WorkItems";
import {
  containerVariantsSlow as containerVariants,
  childVariantsSlow as childVariants,
} from "../../shared/utils";

const Works = ({ onLoadingChange }) => {
  const { data, loading } = useProjectsData();
  const { adminMode, isAdmin, projectsDraft, saveProjectsDraft } = useAdmin();
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [activeIndex, setActiveIndex] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [isNewProject, setIsNewProject] = useState(false);

  // Helper: Convert Firestore object representation to array
  const toArray = (data) => {
    if (Array.isArray(data)) return data;
    if (typeof data === "object" && data !== null) {
      return Object.values(data).filter((item) => typeof item === "object");
    }
    return [];
  };

  const projectsData = React.useMemo(
    () =>
      isAdmin && projectsDraft
        ? Array.isArray(projectsDraft)
          ? projectsDraft
          : toArray(projectsDraft)
        : data?.items
          ? toArray(data.items)
          : [],
    [data, isAdmin, projectsDraft],
  );

  // Notify parent component of loading state
  React.useEffect(() => {
    onLoadingChange?.(loading || !projectsData.length);
  }, [loading, projectsData.length, onLoadingChange]);
  // Extract unique categories from projects
  const projectsNav = React.useMemo(() => {
    if (!projectsData.length) return [{ name: "all" }];

    const categories = ["all"];
    projectsData.forEach((project) => {
      if (
        project.category &&
        !categories.includes(project.category.toLowerCase())
      ) {
        categories.push(project.category.toLowerCase());
      }
    });
    return categories.map((cat) => ({ name: cat }));
  }, [projectsData]);

  const filteredProjects = React.useMemo(() => {
    if (selectedCategory === "all") {
      return [...projectsData].sort((a, b) => b.id - a.id);
    }
    return projectsData.filter(
      (project) =>
        project.category.toLowerCase() === selectedCategory.toLowerCase(),
    );
  }, [selectedCategory, projectsData]);

  const handleClick = (index, categoryName) => {
    setSelectedCategory(categoryName.toLowerCase());
    setActiveIndex(index);
  };

  const handleEditProject = (project) => {
    setEditingProject(project);
    setIsNewProject(false);
    setIsModalOpen(true);
  };

  const handleAddNewProject = () => {
    setEditingProject(null);
    setIsNewProject(true);
    setIsModalOpen(true);
  };

  const handleSaveProject = (updatedProject) => {
    const currentProjects =
      projectsDraft || (data?.items ? toArray(data.items) : []);
    let updatedProjects;

    if (isNewProject) {
      updatedProjects = [...currentProjects, updatedProject];
    } else {
      updatedProjects = currentProjects.map((proj) =>
        proj.id === updatedProject.id ? updatedProject : proj,
      );
    }

    saveProjectsDraft(updatedProjects).catch((error) =>
      console.error("Error saving projects draft:", error),
    );
    setIsModalOpen(false);
  };

  const handleDeleteProject = (projectId) => {
    if (!window.confirm("Are you sure you want to delete this project?"))
      return;

    const currentProjects =
      projectsDraft || (data?.items ? toArray(data.items) : []);
    const updatedProjects = currentProjects.filter(
      (proj) => proj.id !== projectId,
    );
    saveProjectsDraft(updatedProjects).catch((error) =>
      console.error("Error saving projects draft:", error),
    );
  };

  if (loading || !projectsData.length) {
    return <WorksSkeleton data={data} />;
  }

  return (
    <motion.div initial="hidden" animate="visible" variants={containerVariants}>
      <motion.div className="work__filters" variants={childVariants}>
        <div className="work__filters-inner">
          {projectsNav.map((item, index) => (
            <span
              key={index}
              onClick={() => handleClick(index, item.name)}
              className={`work__item ${
                activeIndex === index ? "active-work" : ""
              }`}
            >
              {item.name}
            </span>
          ))}
        </div>
      </motion.div>

      <motion.div className="work__container grid" variants={containerVariants}>
        {filteredProjects.map((item) => (
          <WorkItems
            key={item.id}
            item={item}
            onEdit={adminMode ? handleEditProject : null}
            onDelete={adminMode ? handleDeleteProject : null}
          />
        ))}

        {/* Add New Project Card */}
        {adminMode && (
          <motion.div
            variants={childVariants}
            className="work__card"
            onClick={handleAddNewProject}
            style={{
              border: "var(--border-color)",
              cursor: "pointer",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              minHeight: "250px",
              textAlign: "center",
              borderRadius: "1.25rem",
            }}
          >
            <motion.div
              style={{
                width: "45px",
                height: "45px",
                borderRadius: "50%",
                backgroundColor: "var(--title-color)",
                color: "var(--body-color)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                marginBottom: "1rem",
              }}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
            >
              <i
                className="bx bx-plus"
                style={{
                  fontSize: "2rem",
                  color: "var(--body-color)",
                }}
              ></i>
            </motion.div>
            <span
              style={{
                color: "var(--title-color)",
                fontSize: "0.875rem",
                fontWeight: "500",
              }}
            >
              Add New Project
            </span>
          </motion.div>
        )}
      </motion.div>

      {/* Edit Project Modal */}
      <EditProjectModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        projectData={editingProject}
        onSave={handleSaveProject}
        isNewProject={isNewProject}
      />
    </motion.div>
  );
};

export default Works;
